import React, { useState, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  isToday,
  startOfWeek,
  endOfWeek,
  parseISO,
  isWithinInterval,
  isBefore,
  isAfter
} from 'date-fns';
import { useApp } from '../context/AppContext';

export const ProjectCalendar = () => {
  const { data } = useApp();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week, agenda
  const [selectedFilters, setSelectedFilters] = useState({
    ministries: [],
    eventTypes: [],
    severity: []
  });
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Comprehensive calendar events from all modules
  const calendarEvents = useMemo(() => {
    const events = [];

    // Project Milestones
    data.projects.forEach(project => {
      const milestones = [
        {
          name: 'Site Mobilization',
          dueDate: '2024-07-01',
          status: 'Completed'
        },
        {
          name: 'Earthworks & Foundation',
          dueDate: '2024-10-15',
          status: 'Completed'
        },
        {
          name: 'Structural Framing',
          dueDate: '2025-02-01',
          status: 'On Track'
        },
        {
          name: 'Roofing & Utilities',
          dueDate: '2025-06-15',
          status: 'Pending'
        }
      ];

      milestones.forEach(milestone => {
        events.push({
          id: `${project.id}-${milestone.name}`,
          type: 'milestone',
          title: `${project.name} - ${milestone.name}`,
          date: parseISO(milestone.dueDate),
          project: project.name,
          projectId: project.id,
          ministry: project.ministry,
          status: milestone.status,
          severity: milestone.status === 'Completed' ? 'success' :
                   milestone.status === 'On Track' ? 'normal' :
                   milestone.status === 'At Risk' ? 'warning' : 'critical',
          description: `Project milestone: ${milestone.name}`,
          category: 'Project Milestone'
        });
      });
    });

    // Compliance Deadlines
    const complianceDeadlines = [
      {
        title: 'Contract Award Publication',
        startDate: '2025-01-15',
        daysAllowed: 7,
        projectId: 'NAM-2025-0012',
        ministry: 'Ministry of Works & Transport',
        legalBasis: 'PP Regs 2017, Reg 42(3)'
      },
      {
        title: 'PPU Non-Compliance Acknowledgment',
        startDate: '2025-01-05',
        daysAllowed: 7,
        projectId: 'NAM-2025-0012',
        ministry: 'Ministry of Works & Transport',
        legalBasis: 'PPA S.7(4)'
      },
      {
        title: 'NDP6 Quarterly Progress Report',
        startDate: '2024-12-31',
        daysAllowed: 30,
        ministry: 'All Ministries',
        legalBasis: 'NDP6 IMEP Framework'
      },
      {
        title: 'Treasury Funds Certification',
        startDate: '2025-01-18',
        daysAllowed: 14,
        projectId: 'NAM-2025-0045',
        ministry: 'Ministry of Health & Social Services',
        legalBasis: 'SFA S.14'
      },
      {
        title: 'Annual Procurement Plan Submission',
        startDate: '2024-12-01',
        daysAllowed: 60,
        ministry: 'Ministry of Education',
        legalBasis: 'PPA S.22(1)'
      }
    ];

    complianceDeadlines.forEach((deadline, idx) => {
      const startDate = parseISO(deadline.startDate);
      const dueDate = new Date(startDate);
      dueDate.setDate(dueDate.getDate() + deadline.daysAllowed);
      const now = new Date();
      const isOverdue = isAfter(now, dueDate);
      const daysRemaining = Math.floor((dueDate - now) / (1000 * 60 * 60 * 24));

      events.push({
        id: `compliance-${idx}`,
        type: 'compliance',
        title: deadline.title,
        date: dueDate,
        project: deadline.projectId || 'N/A',
        projectId: deadline.projectId,
        ministry: deadline.ministry,
        status: isOverdue ? 'Overdue' : daysRemaining <= 2 ? 'Critical' : daysRemaining <= 5 ? 'Warning' : 'On Track',
        severity: isOverdue ? 'critical' : daysRemaining <= 2 ? 'critical' : daysRemaining <= 5 ? 'warning' : 'normal',
        description: `Statutory deadline: ${deadline.title}`,
        legalBasis: deadline.legalBasis,
        category: 'Compliance Deadline'
      });
    });

    // NDP6 Reporting Deadlines
    const ndp6Deadlines = [
      {
        title: 'Q1 FY 2024/25 NDP6 Report',
        date: '2024-10-31',
        ministry: 'All Ministries'
      },
      {
        title: 'Q2 FY 2024/25 NDP6 Report',
        date: '2025-01-31',
        ministry: 'All Ministries'
      },
      {
        title: 'Q3 FY 2024/25 NDP6 Report',
        date: '2025-04-30',
        ministry: 'All Ministries'
      },
      {
        title: 'Q4 FY 2024/25 NDP6 Report',
        date: '2025-07-31',
        ministry: 'All Ministries'
      }
    ];

    ndp6Deadlines.forEach((deadline, idx) => {
      const dueDate = parseISO(deadline.date);
      const now = new Date();
      const isOverdue = isAfter(now, dueDate);
      const daysRemaining = Math.floor((dueDate - now) / (1000 * 60 * 60 * 24));

      events.push({
        id: `ndp6-${idx}`,
        type: 'ndp6',
        title: deadline.title,
        date: dueDate,
        ministry: deadline.ministry,
        status: isOverdue ? 'Overdue' : daysRemaining <= 7 ? 'Critical' : daysRemaining <= 14 ? 'Warning' : 'On Track',
        severity: isOverdue ? 'critical' : daysRemaining <= 7 ? 'critical' : daysRemaining <= 14 ? 'warning' : 'normal',
        description: `NDP6 quarterly reporting deadline`,
        category: 'NDP6 Reporting'
      });
    });

    // Budget Reporting Deadlines
    const budgetDeadlines = [
      {
        title: 'Monthly Expenditure Report - January',
        date: '2025-02-10',
        ministry: 'All Ministries'
      },
      {
        title: 'Monthly Expenditure Report - February',
        date: '2025-03-10',
        ministry: 'All Ministries'
      },
      {
        title: 'Mid-Year Budget Review',
        date: '2025-01-15',
        ministry: 'All Ministries'
      }
    ];

    budgetDeadlines.forEach((deadline, idx) => {
      const dueDate = parseISO(deadline.date);
      const now = new Date();
      const isOverdue = isAfter(now, dueDate);
      const daysRemaining = Math.floor((dueDate - now) / (1000 * 60 * 60 * 24));

      events.push({
        id: `budget-${idx}`,
        type: 'budget',
        title: deadline.title,
        date: dueDate,
        ministry: deadline.ministry,
        status: isOverdue ? 'Overdue' : daysRemaining <= 3 ? 'Critical' : daysRemaining <= 7 ? 'Warning' : 'On Track',
        severity: isOverdue ? 'critical' : daysRemaining <= 3 ? 'critical' : daysRemaining <= 7 ? 'warning' : 'normal',
        description: `Budget reporting deadline`,
        category: 'Budget Reporting'
      });
    });

    // Audit Deadlines
    const auditDeadlines = [
      {
        title: 'AG Recommendations Implementation Review',
        date: '2025-02-15',
        ministry: 'Ministry of Works & Transport'
      },
      {
        title: 'Quarterly Audit Committee Meeting',
        date: '2025-03-31',
        ministry: 'All Ministries'
      }
    ];

    auditDeadlines.forEach((deadline, idx) => {
      const dueDate = parseISO(deadline.date);
      const now = new Date();
      const isOverdue = isAfter(now, dueDate);
      const daysRemaining = Math.floor((dueDate - now) / (1000 * 60 * 60 * 24));

      events.push({
        id: `audit-${idx}`,
        type: 'audit',
        title: deadline.title,
        date: dueDate,
        ministry: deadline.ministry,
        status: isOverdue ? 'Overdue' : daysRemaining <= 7 ? 'Critical' : daysRemaining <= 14 ? 'Warning' : 'On Track',
        severity: isOverdue ? 'critical' : daysRemaining <= 7 ? 'critical' : daysRemaining <= 14 ? 'warning' : 'normal',
        description: `Audit compliance deadline`,
        category: 'Audit Compliance'
      });
    });

    return events;
  }, [data.projects]);

  // Filter events
  const filteredEvents = useMemo(() => {
    let filtered = calendarEvents;

    if (selectedFilters.ministries.length > 0) {
      filtered = filtered.filter(event =>
        selectedFilters.ministries.includes(event.ministry)
      );
    }

    if (selectedFilters.eventTypes.length > 0) {
      filtered = filtered.filter(event =>
        selectedFilters.eventTypes.includes(event.type)
      );
    }

    if (selectedFilters.severity.length > 0) {
      filtered = filtered.filter(event =>
        selectedFilters.severity.includes(event.severity)
      );
    }

    return filtered;
  }, [calendarEvents, selectedFilters]);

  // Get events for a specific day
  const getEventsForDay = (day) => {
    return filteredEvents.filter(event =>
      isSameDay(event.date, day)
    );
  };

  // Calendar grid generation
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    return eachDayOfInterval({ start: startDate, end: endDate });
  }, [currentDate]);

  // Event type configuration
  const eventTypeConfig = {
    milestone: {
      label: 'Project Milestone',
      icon: FiIcons.FiFlag,
      color: 'blue'
    },
    compliance: {
      label: 'Compliance Deadline',
      icon: FiIcons.FiShield,
      color: 'red'
    },
    ndp6: {
      label: 'NDP6 Reporting',
      icon: FiIcons.FiTarget,
      color: 'green'
    },
    budget: {
      label: 'Budget Reporting',
      icon: FiIcons.FiDollarSign,
      color: 'amber'
    },
    audit: {
      label: 'Audit Compliance',
      icon: FiIcons.FiFileText,
      color: 'purple'
    }
  };

  const severityConfig = {
    success: { color: 'green', label: 'Completed' },
    normal: { color: 'blue', label: 'On Track' },
    warning: { color: 'amber', label: 'Warning' },
    critical: { color: 'red', label: 'Critical' }
  };

  const handlePreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const handleToday = () => setCurrentDate(new Date());

  const handleFilterToggle = (filterType, value) => {
    setSelectedFilters(prev => {
      const currentFilters = prev[filterType];
      const newFilters = currentFilters.includes(value)
        ? currentFilters.filter(f => f !== value)
        : [...currentFilters, value];
      return { ...prev, [filterType]: newFilters };
    });
  };

  const stats = useMemo(() => {
    const now = new Date();
    return {
      total: filteredEvents.length,
      upcoming: filteredEvents.filter(e => isAfter(e.date, now)).length,
      overdue: filteredEvents.filter(e => isBefore(e.date, now) && e.severity === 'critical').length,
      thisMonth: filteredEvents.filter(e =>
        isWithinInterval(e.date, {
          start: startOfMonth(currentDate),
          end: endOfMonth(currentDate)
        })
      ).length,
      critical: filteredEvents.filter(e => e.severity === 'critical').length
    };
  }, [filteredEvents, currentDate]);

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Project Milestone Calendar</h1>
          <p className="text-slate-500">
            Comprehensive timeline of all project milestones, compliance deadlines, and reporting events
          </p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiFilter} />
            Export Calendar
          </button>
          <button className="px-4 py-2 bg-grn-navy text-white rounded-lg text-sm font-medium hover:bg-blue-900 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiPlus} />
            Add Event
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="text-center p-4">
          <div className="text-3xl font-black text-slate-900">{stats.total}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Total Events</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-blue-500">
          <div className="text-3xl font-black text-blue-700">{stats.upcoming}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Upcoming</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-red-600">
          <div className="text-3xl font-black text-red-700">{stats.overdue}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Overdue</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-green-500">
          <div className="text-3xl font-black text-green-700">{stats.thisMonth}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">This Month</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-orange-500">
          <div className="text-3xl font-black text-orange-700">{stats.critical}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Critical</div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="space-y-4">
          {/* Event Type Filters */}
          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
              Filter by Event Type
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {Object.entries(eventTypeConfig).map(([type, config]) => (
                <button
                  key={type}
                  onClick={() => handleFilterToggle('eventTypes', type)}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 whitespace-nowrap',
                    selectedFilters.eventTypes.includes(type)
                      ? 'bg-grn-navy text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-grn-navy/30'
                  )}
                >
                  <SafeIcon icon={config.icon} />
                  {config.label}
                </button>
              ))}
            </div>
          </div>

          {/* Severity Filters */}
          <div className="border-t border-slate-200 pt-4">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
              Filter by Severity
            </h3>
            <div className="flex gap-2">
              {Object.entries(severityConfig).map(([severity, config]) => (
                <button
                  key={severity}
                  onClick={() => handleFilterToggle('severity', severity)}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap',
                    selectedFilters.severity.includes(severity)
                      ? 'bg-grn-navy text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-grn-navy/30'
                  )}
                >
                  {config.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Calendar Navigation */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handlePreviousMonth}
              className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
            >
              <SafeIcon icon={FiIcons.FiChevronLeft} className="text-xl" />
            </button>
            <h2 className="text-xl font-bold text-slate-900">
              {format(currentDate, 'MMMM yyyy')}
            </h2>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
            >
              <SafeIcon icon={FiIcons.FiChevronRight} className="text-xl" />
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleToday}
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50"
            >
              Today
            </button>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
              {['month', 'week', 'agenda'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={clsx(
                    'px-3 py-1.5 rounded text-xs font-medium transition-all',
                    viewMode === mode
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  )}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        {viewMode === 'month' && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div
                  key={day}
                  className="p-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7">
              {calendarDays.map((day, idx) => {
                const dayEvents = getEventsForDay(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isTodayDate = isToday(day);

                return (
                  <div
                    key={idx}
                    className={clsx(
                      'min-h-[120px] p-2 border-b border-r border-slate-200',
                      !isCurrentMonth && 'bg-slate-50',
                      isTodayDate && 'bg-blue-50'
                    )}
                  >
                    <div
                      className={clsx(
                        'text-sm font-bold mb-2',
                        !isCurrentMonth && 'text-slate-400',
                        isTodayDate
                          ? 'text-blue-700 bg-blue-100 rounded-full w-7 h-7 flex items-center justify-center'
                          : 'text-slate-700'
                      )}
                    >
                      {format(day, 'd')}
                    </div>

                    {/* Day Events */}
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map(event => {
                        const typeConfig = eventTypeConfig[event.type];
                        const severityConf = severityConfig[event.severity];
                        return (
                          <button
                            key={event.id}
                            onClick={() => setSelectedEvent(event)}
                            className={clsx(
                              'w-full text-left px-2 py-1 rounded text-xs font-medium transition-all hover:shadow-md',
                              severityConf.color === 'red'
                                ? 'bg-red-100 text-red-800 border border-red-200'
                                : severityConf.color === 'amber'
                                ? 'bg-amber-100 text-amber-800 border border-amber-200'
                                : severityConf.color === 'green'
                                ? 'bg-green-100 text-green-800 border border-green-200'
                                : 'bg-blue-100 text-blue-800 border border-blue-200'
                            )}
                          >
                            <div className="flex items-center gap-1">
                              <SafeIcon icon={typeConfig.icon} className="text-xs shrink-0" />
                              <span className="truncate">{event.title}</span>
                            </div>
                          </button>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-slate-500 font-medium pl-2">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Agenda View */}
        {viewMode === 'agenda' && (
          <div className="space-y-4">
            {filteredEvents
              .sort((a, b) => a.date - b.date)
              .map(event => {
                const typeConfig = eventTypeConfig[event.type];
                const severityConf = severityConfig[event.severity];
                return (
                  <div
                    key={event.id}
                    className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => setSelectedEvent(event)}
                  >
                    <div className="text-center min-w-[60px]">
                      <div className="text-2xl font-black text-slate-900">
                        {format(event.date, 'd')}
                      </div>
                      <div className="text-xs text-slate-500 uppercase">
                        {format(event.date, 'MMM')}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={clsx(
                            'px-2 py-1 rounded text-xs font-medium',
                            typeConfig.color === 'blue'
                              ? 'bg-blue-100 text-blue-700'
                              : typeConfig.color === 'red'
                              ? 'bg-red-100 text-red-700'
                              : typeConfig.color === 'green'
                              ? 'bg-green-100 text-green-700'
                              : typeConfig.color === 'amber'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-purple-100 text-purple-700'
                          )}
                        >
                          <SafeIcon icon={typeConfig.icon} className="inline mr-1" />
                          {typeConfig.label}
                        </span>
                        <Badge status={event.status} size="sm" />
                      </div>
                      <h4 className="font-bold text-slate-900 mb-1">{event.title}</h4>
                      <p className="text-sm text-slate-600 mb-2">{event.description}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <SafeIcon icon={FiIcons.FiBuilding} />
                          <span>{event.ministry}</span>
                        </div>
                        {event.projectId && (
                          <div className="flex items-center gap-1">
                            <SafeIcon icon={FiIcons.FiBriefcase} />
                            <span className="font-mono">{event.projectId}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </Card>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={clsx(
                      'px-2 py-1 rounded text-xs font-medium',
                      eventTypeConfig[selectedEvent.type].color === 'blue'
                        ? 'bg-blue-100 text-blue-700'
                        : eventTypeConfig[selectedEvent.type].color === 'red'
                        ? 'bg-red-100 text-red-700'
                        : eventTypeConfig[selectedEvent.type].color === 'green'
                        ? 'bg-green-100 text-green-700'
                        : eventTypeConfig[selectedEvent.type].color === 'amber'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-purple-100 text-purple-700'
                    )}
                  >
                    <SafeIcon icon={eventTypeConfig[selectedEvent.type].icon} className="inline mr-1" />
                    {selectedEvent.category}
                  </span>
                  <Badge status={selectedEvent.status} size="sm" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">{selectedEvent.title}</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {format(selectedEvent.date, 'EEEE, MMMM d, yyyy')}
                </p>
              </div>
              <button
                onClick={() => setSelectedEvent(null)}
                className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"
              >
                <SafeIcon icon={FiIcons.FiX} className="text-xl" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                  Description
                </h3>
                <p className="text-slate-600">{selectedEvent.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                    Ministry
                  </h3>
                  <p className="text-slate-900">{selectedEvent.ministry}</p>
                </div>
                {selectedEvent.projectId && (
                  <div>
                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                      Project ID
                    </h3>
                    <p className="text-slate-900 font-mono">{selectedEvent.projectId}</p>
                  </div>
                )}
              </div>

              {selectedEvent.legalBasis && (
                <div>
                  <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">
                    Legal Basis
                  </h3>
                  <p className="text-slate-900 font-mono text-sm">{selectedEvent.legalBasis}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50">
              <button
                onClick={() => setSelectedEvent(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-white transition-colors"
              >
                Close
              </button>
              <button className="px-4 py-2 bg-grn-navy text-white rounded-lg text-sm font-medium hover:bg-blue-900 transition-colors flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiEye} />
                View Details
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <Card className="bg-slate-50 border-slate-200">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">
          Calendar Legend
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(eventTypeConfig).map(([type, config]) => (
            <div key={type} className="flex items-center gap-2">
              <SafeIcon
                icon={config.icon}
                className={clsx(
                  'text-lg',
                  config.color === 'blue' && 'text-blue-600',
                  config.color === 'red' && 'text-red-600',
                  config.color === 'green' && 'text-green-600',
                  config.color === 'amber' && 'text-amber-600',
                  config.color === 'purple' && 'text-purple-600'
                )}
              />
              <span className="text-sm text-slate-700">{config.label}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};